import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Restaurant, Table, TableOrder } from "../pages/api";
import { SocketUpdateType } from "../models";
import type { SocketUpdate } from "../models";

const initialState: Restaurant = {
  activeTables: [],
  activeOrders: [],
};

export const restaurantSlice = createSlice({
  name: "restaurant",
  initialState,
  reducers: {
    setActiveTables: (state: any, action: PayloadAction<Array<Table>>) => {
      state.activeTables = action.payload;
    },
    setActiveOrders: (state: any, action: PayloadAction<Array<TableOrder>>) => {
      state.activeOrders = action.payload;
    },
    applySocketUpdate: (state: any, action: PayloadAction<SocketUpdate>) => {
      const update = action.payload;

      switch (update.type) {
        case SocketUpdateType.CREATE_TABLE: {
          const data = update.data.data;
          state.activeTables = [
            ...state.activeTables.filter((t: Table) => t.id !== data.id),
            data,
          ].sort((a: Table, b: Table) => a.name.localeCompare(b.name));
          break;
        }
        case SocketUpdateType.UPDATE_TABLE: {
          const data = update.data.data;
          if (!data.isActive) {
            state.activeTables = state.activeTables.filter(
              (table: Table) => table.id !== data.id,
            );
            break;
          }

          const existingTable = state.activeTables.find(
            (table: Table) => table.id === data.id,
          );
          if (existingTable) {
            state.activeTables = state.activeTables
              .map((table: Table) =>
                table.id === data.id ? { ...table, ...data } : table,
              )
              .sort((a: Table, b: Table) => a.name.localeCompare(b.name));
          } else if (typeof data.name === "string") {
            state.activeTables = [...state.activeTables, data].sort(
              (a: Table, b: Table) => a.name.localeCompare(b.name),
            );
          }
          break;
        }
        case SocketUpdateType.DELETE_TABLE: {
          const { ids } = update.data.data;
          const deletedIds = Array.isArray(ids) ? ids : [ids];
          state.activeTables = state.activeTables.filter(
            (table: Table) => !deletedIds.includes(table.id),
          );
          break;
        }
        case SocketUpdateType.CREATE_ORDER: {
          const data = update.data.data;
          state.activeOrders = [
            ...state.activeOrders.filter(
              (order: TableOrder) => order.id !== data.id,
            ),
            data,
          ].sort((a: TableOrder, b: TableOrder) =>
            (b.billNumber || "").localeCompare(a.billNumber || ""),
          );
          break;
        }
        case SocketUpdateType.UPDATE_ORDER: {
          const data = update.data.data;
          const removedProductIds = data.removedProductIds || [];
          if (removedProductIds.length > 0) {
            state.activeOrders = state.activeOrders.map((order: TableOrder) => {
              if (order.id !== data.id) return order;

              const removedProducts = (order.products || [])
                .filter((product) => removedProductIds.includes(product.id))
                .map((product) => ({ ...product, isRemoved: true }));

              return {
                ...data,
                products: [...(data.products || []), ...removedProducts],
              };
            });
          } else {
            state.activeOrders = data.closedAt
              ? state.activeOrders.filter(
                  (order: TableOrder) => order.id !== data.id,
                )
              : state.activeOrders.map((order: TableOrder) =>
                  order.id === data.id ? data : order,
                );
          }
          break;
        }
        case SocketUpdateType.DELETE_ORDER: {
          const { ids } = update.data.data;
          const deletedIds = Array.isArray(ids) ? ids : [ids];
          state.activeOrders = state.activeOrders.filter(
            (order: TableOrder) => !deletedIds.includes(order.id),
          );
          break;
        }
        case SocketUpdateType.GENERATE_BILL: {
          const data = update.data.data;
          state.activeOrders = state.activeOrders.map((order: TableOrder) =>
            order.id === data.orderId
              ? { ...order, isGenerateBill: data.isGenerateBill }
              : order,
          );
          break;
        }
        case SocketUpdateType.COMPLETE_ORDER: {
          const data = update.data.data;
          if (data.isCleaning && data.tableId) {
            state.activeTables = state.activeTables.map((table: Table) =>
              table.id === data.tableId
                ? { ...table, isCleaning: true }
                : table,
            );
          }
          state.activeOrders = state.activeOrders.filter(
            (order: TableOrder) => order.id !== data.orderId,
          );
          break;
        }
        case SocketUpdateType.UPDATE_ORDER_PRODUCT: {
          const data = update.data.data;
          state.activeOrders = state.activeOrders.map((order: TableOrder) =>
            order.id === data.orderId
              ? {
                  ...order,
                  products: order.products.map((product) =>
                    product.id === data.orderProductId
                      ? data.orderProduct
                      : product,
                  ),
                }
              : order,
          );
          break;
        }
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const { setActiveTables, setActiveOrders, applySocketUpdate } =
  restaurantSlice.actions;

export default restaurantSlice.reducer;
